import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api";

/**
 * A 401 means the session is gone, wherever it happens.
 *
 * The api client has always raised `isAuthError`, but nothing acted on it, so an
 * expired cookie mid-session produced inline "your session has expired" errors on
 * every list while the app kept serving stale cached data from its five-minute
 * `staleTime` and never offered a way back in. This handles it once, globally:
 * clear the cache so no stale data survives into the next sign-in, and go to the
 * sign-in screen.
 */
let redirecting = false;

function handleAuthFailure(error: unknown): void {
  if (!(error instanceof ApiError) || !error.isAuthError) return;
  if (redirecting) return;
  if (window.location.pathname === "/login") return;
  redirecting = true;
  queryClient.clear();
  window.location.assign("/login");
}

/**
 * Query defaults tuned for an admin tool.
 *
 * `retry` deliberately does NOT retry 4xx: a 403 or a validation failure is a
 * final answer, and retrying it just delays the error the operator needs to see.
 * Network and 5xx errors do retry, once.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleAuthFailure }),
  mutationCache: new MutationCache({ onError: handleAuthFailure }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 1;
      },
    },
    mutations: {
      retry: 0,
    },
  },
});

/**
 * Query keys in one place, so invalidation after a mutation is never a guess.
 * Keys are arrays prefixed by the resource, which makes prefix invalidation work:
 * `invalidateQueries({ queryKey: keys.orders.all })` clears every order query.
 */
export const keys = {
  session: ["session"] as const,

  dashboard: (days: number) => ["dashboard", days] as const,

  products: {
    all: ["products"] as const,
    list: (filters: Record<string, unknown>) => ["products", "list", filters] as const,
    detail: (id: number) => ["products", "detail", id] as const,
  },
  orders: {
    all: ["orders"] as const,
    list: (filters: Record<string, unknown>) => ["orders", "list", filters] as const,
    detail: (id: number) => ["orders", "detail", id] as const,
  },
  categories: {
    all: ["categories"] as const,
    list: (filters: Record<string, unknown>) => ["categories", "list", filters] as const,
  },
  coupons: {
    all: ["coupons"] as const,
    list: (filters: Record<string, unknown>) => ["coupons", "list", filters] as const,
  },
  customers: {
    all: ["customers"] as const,
    list: (filters: Record<string, unknown>) => ["customers", "list", filters] as const,
  },
  deliveryAreas: {
    all: ["delivery-areas"] as const,
  },
  media: {
    all: ["media"] as const,
    list: (filters: Record<string, unknown>) => ["media", "list", filters] as const,
  },
  settings: ["settings"] as const,
  staff: ["staff"] as const,
  profile: ["profile"] as const,
};
